/**
 * Nummora upsells: cart drawer "Complete your set" (+ Add buttons) and product page
 * "Complete your kit" add-on checkboxes. Uses Shopify standard cart events so Horizon's
 * cart drawer, cart icon and fly-to-cart refresh themselves.
 * Cart writes share one queue with nm-gift.js (window.nmCartQueue): Shopify applies simultaneous
 * cart changes as "last write wins", so an add-on sent at the same moment as the free gift could be lost.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const routes = () => (window.Theme && window.Theme.routes) || {};
const jsUrl = (url, fallback) => (url || fallback).replace(/\.js$/, '') + '.js';

/** Runs cart writes one at a time (shared with nm-gift.js). */
if (!window.nmCartQueue) {
  let tail = Promise.resolve();
  window.nmCartQueue = (task) => {
    const run = tail.then(task, task);
    tail = run.catch(() => {});
    return run;
  };
}
const queue = (task) => window.nmCartQueue(task);

function sectionIds() {
  const ids = new Set(['cart-drawer-section']);
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => ids.add(el.dataset.sectionId));
  return [...ids];
}

const getCart = async () =>
  (await fetch(jsUrl(routes().cart_url, '/cart'), { headers: { Accept: 'application/json' } })).json();

const postAdd = (items) =>
  fetch(jsUrl(routes().cart_add_url, '/cart/add'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items, sections: sectionIds().join(',') }),
  });

export async function nmAddLines(items, source) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity || 1 })),
      promise: deferred.promise,
    })
  );
  try {
    const result = await queue(async () => {
      const before = await getCart();
      const qty = (cart, id) => cart.items.filter((l) => l.variant_id === id).reduce((n, l) => n + l.quantity, 0);
      let res = await postAdd(items);
      let data = await res.json();
      let cart = await getCart();
      let failed = !res.ok || Boolean(data.status);
      // Safety net: re-send anything that did not stick (e.g. overwritten by another cart change).
      if (!failed) {
        const missing = items.filter((i) => qty(cart, i.id) < qty(before, i.id) + (i.quantity || 1));
        if (missing.length) {
          res = await postAdd(missing);
          data = await res.json();
          cart = await getCart();
          failed = !res.ok || Boolean(data.status);
        }
      }
      return { cart, data, failed };
    });
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result.cart),
      detail: {
        items: result.cart.items,
        source,
        sections: result.failed ? undefined : result.data.sections,
        itemCount: items.reduce((n, i) => n + (i.quantity || 1), 0),
        didError: result.failed,
      },
    });
    return !result.failed;
  } catch (error) {
    deferred.reject(error);
    return false;
  }
}

/* Product page: keep the frame add-on in sync with the selected kit variant */
function currentVariantId(box) {
  const pid = box.dataset.productId;
  const form = document.querySelector(`product-form-component[data-product-id="${pid}"]`) || document.querySelector('product-form-component');
  const input = form && form.querySelector('input[name="id"]');
  return input ? input.value : new URLSearchParams(location.search).get('variant');
}

function syncFrames() {
  document.querySelectorAll('[data-nm-addons]').forEach((box) => {
    const row = box.querySelector('[data-nm-frame-row]');
    const mapEl = box.querySelector('[data-nm-frame-map]');
    if (!row || !mapEl) return;
    let map = {};
    try { map = JSON.parse(mapEl.textContent); } catch (e) { return; }
    const match = map[String(currentVariantId(box))];
    const check = row.querySelector('input[type="checkbox"]');
    const meta = row.querySelector('[data-nm-frame-meta]');
    if (match) {
      check.value = String(match[0]);
      if (meta) meta.textContent = 'Fits ' + match[1];
      row.hidden = false;
    } else {
      check.checked = false;
      row.hidden = true;
    }
  });
}

let syncTimer;
function scheduleSync() {
  clearTimeout(syncTimer);
  let tries = 0;
  const start = String(currentVariantId(document.querySelector('[data-nm-addons]') || document.body));
  const tick = () => {
    tries += 1;
    const box = document.querySelector('[data-nm-addons]');
    if (!box) return;
    if (String(currentVariantId(box)) !== start || tries > 20) return syncFrames();
    syncTimer = setTimeout(tick, 100);
  };
  syncTimer = setTimeout(tick, 100);
}

if (!window.__nmUpsellBound) {
  window.__nmUpsellBound = true;

  // Cart drawer: one-tap add
  document.addEventListener('click', async (event) => {
    const btn = event.target instanceof Element ? event.target.closest('[data-nm-upsell-add]') : null;
    if (!btn || btn.disabled) return;
    event.preventDefault();
    const label = btn.querySelector('.nm-upsell__add-label');
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    if (label) label.textContent = btn.dataset.addingText || 'Adding';
    const ok = await nmAddLines([{ id: Number(btn.dataset.variantId), quantity: 1 }], 'nm-upsell-drawer');
    if (!ok && btn.isConnected) {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      if (label) label.textContent = btn.dataset.addText || 'Add';
    }
  });

  // Product page: follow variant changes
  document.addEventListener('change', (event) => {
    if (event.target instanceof Element && event.target.closest('variant-picker')) scheduleSync();
  });
  syncFrames();

  // Product page: add ticked add-ons right after the main product is added
  document.addEventListener(CartLinesUpdateEvent.eventName, (event) => {
    const form = event.target instanceof Element ? event.target.closest('product-form-component') : null;
    if (!form) return;
    const boxes = [...document.querySelectorAll('[data-nm-addons]')].filter(
      (b) => !form.dataset.productId || b.dataset.productId === String(form.dataset.productId)
    );
    const checked = boxes.flatMap((b) =>
      [...b.querySelectorAll('input[type="checkbox"]:checked')].filter((c) => !c.closest('[hidden]'))
    );
    if (!checked.length || !event.promise) return;
    const items = checked.map((c) => ({ id: Number(c.value), quantity: 1 }));
    event.promise
      .then((result) => {
        if (result && result.detail && result.detail.didError) return;
        checked.forEach((c) => (c.checked = false));
        return nmAddLines(items, 'nm-addons');
      })
      .catch(() => {});
  });
}
